import { getOrganizationBearerHeaders } from "@/utils/auth/organization-token"
import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

import {
  buildCoviSummaryPreferencesRequest,
  parseCoviSummaryMemoryResponse,
  SUMMARY_MEMORY_API_PATH,
  type CoviSummaryMemory,
} from "./summaryMemory"

type SummaryMemoryHeaders = Record<string, string>

/** Pins requests to the edited organization and rejects a conflicting tab route before minting its bearer token. */
export async function getSummaryMemoryAuthorizationHeaders(
  orgSlug: string | null | undefined,
): Promise<SummaryMemoryHeaders> {
  if (!orgSlug) {
    return {}
  }

  if (typeof window !== "undefined") {
    const routeOrganizationSlug = getRouteOrganizationSlug(
      window.location.pathname,
    )
    if (routeOrganizationSlug !== null && routeOrganizationSlug !== orgSlug) {
      throw new Error(
        `This tab shows organization "${routeOrganizationSlug}", not "${orgSlug}".`,
      )
    }
  }

  return getOrganizationBearerHeaders(orgSlug)
}

// Naming the org keeps background tabs and cross-org visits scoped to the route.
function summaryMemoryUrl(organizationSlug: string): string {
  return `${SUMMARY_MEMORY_API_PATH}?organization=${encodeURIComponent(organizationSlug)}`
}

export async function fetchSummaryMemory(
  headers: SummaryMemoryHeaders,
  organizationSlug: string,
  signal: AbortSignal,
): Promise<CoviSummaryMemory> {
  const response = await fetch(summaryMemoryUrl(organizationSlug), {
    method: "GET",
    headers,
    signal,
  })
  if (!response.ok) {
    throw new Error(`Failed to load summary memory (${response.status})`)
  }
  return parseCoviSummaryMemoryResponse(await response.json())
}

export async function saveSummaryMemory(
  memory: CoviSummaryMemory,
  headers: SummaryMemoryHeaders,
  signal: AbortSignal,
  organizationSlug: string,
): Promise<CoviSummaryMemory> {
  const response = await fetch(summaryMemoryUrl(organizationSlug), {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(buildCoviSummaryPreferencesRequest(memory)),
    signal,
  })
  if (!response.ok) {
    throw new Error(`Failed to save summary memory (${response.status})`)
  }
  return parseCoviSummaryMemoryResponse(await response.json())
}

export async function deleteSummaryMemory(
  headers: SummaryMemoryHeaders,
  signal: AbortSignal,
  organizationSlug: string,
): Promise<CoviSummaryMemory> {
  const response = await fetch(summaryMemoryUrl(organizationSlug), {
    method: "DELETE",
    headers,
    signal,
  })
  if (!response.ok) {
    throw new Error(`Failed to clear summary memory (${response.status})`)
  }
  return parseCoviSummaryMemoryResponse(await response.json())
}
